import { Area } from 'react-easy-crop'
import chroma from 'chroma-js'
import {
  isVideoUrlShared,
  transformTipsyImage,
} from '@/constant/prefetch-home-image'

export async function convertImageToDataURL(imageUrl: string): Promise<string> {
  if (typeof window !== 'undefined') {
    try {
      const response = await fetch(imageUrl)
      const blob = await response.blob()

      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          if (reader.result) {
            resolve(reader.result.toString())
          } else {
            reject(new Error('Failed to read the image file.'))
          }
        }
        reader.onerror = () => {
          reject(new Error('Error occurred while reading the image file.'))
        }
        reader.readAsDataURL(blob)
      })
    } catch (error) {
      throw new Error('Failed to fetch the image.')
    }
  } else {
    try {
      const response = await fetch(imageUrl)
      const arrayBuffer = await response.arrayBuffer()
      const buffer = Buffer.from(arrayBuffer)
      const base64 = buffer.toString('base64')
      const mimeType = response.headers.get('content-type')
      return `data:${mimeType};base64,${base64}`
    } catch (error) {
      throw new Error('Failed to fetch the image.')
    }
  }
}

export async function getLocalImgUrl(imageUrl: string): Promise<string> {
  try {
    return convertImageToDataURL(imageUrl)
  } catch (error) {
    console.error('convertImageToDataURL error:', error)
    return imageUrl
  }
}

// 将 Data URL 转换为 Blob 对象的函数
export function dataURLToBlob(dataURL: string): Blob {
  const parts: string[] = dataURL.split(';base64,')
  const imageType: string = parts[0].split(':')[1]
  const decodedData: string = atob(parts[1])
  const byteNumbers: number[] = new Array(decodedData.length)

  for (let i = 0; i < decodedData.length; i++) {
    byteNumbers[i] = decodedData.charCodeAt(i)
  }

  const byteArray: Uint8Array = new Uint8Array(byteNumbers)

  return new Blob([byteArray as BlobPart], { type: imageType })
}

/**
 * 从DataURL获取图片的宽度和高度
 * @param dataURL 图片的DataURL字符串
 * @returns Promise，解析为包含宽度和高度的对象
 */
export async function getImageSizeFromDataURL(
  dataURL: string
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.width, height: img.height })
    }
    img.onerror = (error) => {
      reject(new Error('Image loading failed'))
    }
    img.src = dataURL
  })
}

// 由矩形裁剪框转换为方形裁剪框，保持中心重叠同时扩展正方形裁剪框的边长，确保不超过矩形的较长边且尽可能大
export function calculateSquareCrop(
  imgWidth: number,
  imgHeight: number,
  x: number,
  y: number,
  width: number,
  height: number
): Area {
  // 确定矩形裁剪框的中心点
  const centerX = x + width / 2
  const centerY = y + height / 2

  // 较长边的一半
  const halfLongerSide = Math.max(width, height) / 2

  const squareSide =
    Math.min(
      centerX, // 到左边界的最大距离
      imgWidth - centerX, // 到右边界的最大距离
      centerY, // 到上边界的最大距离
      imgHeight - centerY, // 到下边界的最大距离
      halfLongerSide // 确保正方形边长不超过矩形的较长边
    ) * 2

  // 计算正方形裁剪框的x, y坐标
  const squareX = centerX - squareSide / 2
  const squareY = centerY - squareSide / 2

  return {
    x: Math.trunc(squareX),
    y: Math.trunc(squareY),
    width: Math.trunc(squareSide),
    height: Math.trunc(squareSide),
  }
}

export const getInitialCroppedArea = (
  imageWidth: number,
  imageHeight: number
): Area => {
  const aspectRatio = 1
  const width = Math.min(imageWidth, imageHeight * aspectRatio)
  const height = width / aspectRatio
  const x = (imageWidth - width) / 2
  const y = (imageHeight - height) / 2
  return { x, y, width, height }
}

// 获取裁剪后的图片 dataUrl
export async function getCroppedImgDataUrl(
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number }
): Promise<string> {
  // 创建一个新的 Image 对象
  const image = new Image()
  // 设置跨域访问
  image.crossOrigin = 'anonymous'
  // 设置图片的源路径
  image.src = imageSrc
  // 确保图片加载完成
  await new Promise((resolve, reject) => {
    image.onload = resolve
    image.onerror = reject
  })
  // 创建一个 canvas 元素并设置其宽度和高度
  const canvas = document.createElement('canvas')
  canvas.width = pixelCrop.width
  canvas.height = pixelCrop.height
  // 获取 canvas 的上下文
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Unable to get canvas context')
  }
  // 使用 drawImage 方法将图片的指定部分绘制到 canvas 上
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  )
  // 使用 toDataURL 方法将 canvas 内容转换成 data URL 并返回
  return canvas.toDataURL('image/jpeg')
}
// 获取裁剪并压缩后的图片File
export async function getCroppedImgFile(
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number },
  quality: number = 0.9
): Promise<File | null> {
  // 创建一个新的 Image 对象
  const image = new Image()
  // 设置跨域访问
  image.crossOrigin = 'anonymous'
  // 设置图片的源路径
  image.src = imageSrc
  // 确保图片加载完成
  await new Promise((resolve, reject) => {
    image.onload = resolve
    image.onerror = reject
  })
  // 创建一个 canvas 元素并设置其宽度和高度
  const canvas = document.createElement('canvas')
  canvas.width = pixelCrop.width
  canvas.height = pixelCrop.height
  // 获取 canvas 的上下文
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Unable to get canvas context')
  }
  // 使用 drawImage 方法将图片的指定部分绘制到 canvas 上
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  )
  // 将 canvas 内容转换成 File 并返回
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `cropped_image.jpg`, {
            type: 'image/jpeg',
          })
          resolve(file)
        } else {
          resolve(null) // 如果 blob 为 null，则返回 null
        }
      },
      'image/jpeg',
      quality
    ) // 确保指定生成图片的格式
  })
}

export function transformImage(url: string, width?: number, quality = 90) {
  if (isVideoUrl(url)) return url
  // URL 构造委托共享纯函数（constant/prefetch-home-image.ts），与 SW 图片预取同调一份，
  // 保证 SW 预取的图 URL 与页面 <img> 实际请求字节级一致（否则 HTTP 缓存不命中、预取白费）。
  return transformTipsyImage(url, width, quality)
}

export function transformImageAuto(url: string, width?: number, quality = 90) {
  // eslint-disable-next-line no-useless-escape
  const regex = /(https?:\/\/[^\/]+\.tipsy\.chat)(?!.*\/cdn-cgi\/image\/)(\/.*)/
  const transWidth = width ? `width=${width},` : ''
  const qualityParam = quality !== 90 ? `quality=${quality},` : ''
  const newPath = `/cdn-cgi/image/${transWidth}${qualityParam}f=auto`

  const match = url?.match(regex)
  if (match) {
    return `${match[1]}${newPath}${match[2]}`
  }
  return url
}

/**
 * 为响应式图片生成优化的 URL
 * @param url 原始图片 URL
 * @param sizes 不同断点的宽度配置 { mobile: 750, tablet: 1024, desktop: 1920 }
 * @param quality 图片质量 (1-100)
 * @returns 优化后的 URL
 */
export function transformImageResponsive(
  url: string,
  sizes: { mobile?: number; tablet?: number; desktop?: number } = {},
  quality = 90
) {
  const defaultSizes = {
    mobile: sizes.mobile || 750,
    tablet: sizes.tablet || 1024,
    desktop: sizes.desktop || 1920,
  }

  // 根据当前设备选择合适的尺寸
  if (typeof window !== 'undefined') {
    const width = window.innerWidth
    let targetWidth = defaultSizes.desktop

    if (width <= 768) {
      targetWidth = defaultSizes.mobile
    } else if (width <= 1024) {
      targetWidth = defaultSizes.tablet
    }

    return transformImageAuto(url, targetWidth, quality)
  }

  // 服务端渲染时返回桌面版尺寸
  return transformImageAuto(url, defaultSizes.desktop, quality)
}

export async function convertImageToJPEG(
  imageFile: File,
  quality: number = 90
): Promise<File | null> {
  const img = new Image()
  img.crossOrigin = 'Anonymous'
  img.src = URL.createObjectURL(imageFile)
  await new Promise((resolve, reject) => {
    img.onload = resolve
    img.onerror = reject
  })
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  canvas.width = img.width
  canvas.height = img.height
  if (!ctx) {
    throw new Error('Unable to get canvas context')
  }
  ctx.drawImage(img, 0, 0)
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const newFile = new File([blob], 'converted_image.jpeg', {
            type: 'image/jpeg',
          })
          resolve(newFile)
        } else {
          resolve(null)
        }
      },
      'image/jpeg',
      quality
    )
  })
}

export function adjustPrimaryColor(
  color: string,
  threshold: number = 0.5
): string {
  const hsl = chroma(color).hsl()
  // eslint-disable-next-line prefer-const
  let [h, s, l] = hsl

  if (l > threshold) {
    l = threshold
  }

  return chroma.hsl(h, s, l).hex()
}

export function darken(color: string, factor: number = 1): string {
  if (!color) return ''
  return chroma(color).darken(factor).hex()
}

export function brighten(color: string, factor: number = 1): string {
  if (!color) return ''
  return chroma(color).brighten(factor).hex()
}

export function hexToRgb(hex: string): [number, number, number] {
  let trasferHex = hex.replace(/^#/, '')
  if (trasferHex.length === 3) {
    trasferHex = trasferHex
      .split('')
      .map((char) => char + char)
      .join('')
  }
  const num = parseInt(trasferHex, 16)
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255]
}

export function transferWebP2GIF(url: string, width = 160, quality = 90) {
  if (!url) return url
  if (isVideoUrl(url)) return url
  // 动图地址必须稳定；src 一旦变化，浏览器会重新加载并从首帧开始播放
  // 这里不再按 UA 分叉，但保留 CDN resize/quality，兼顾播放稳定性和体积。
  // URL 构造委托共享纯函数（constant/prefetch-home-image.ts），与 SW 图片预取同调一份。
  return transformTipsyImage(url, width, quality)
}

export function getAnimatedImageDisplayUrl(
  animatedUrl: string,
  fallbackUrl = '',
  width?: number,
  quality = 90
) {
  // animatedUrl 存在时优先返回稳定的动图地址；没有动图才回退到静态封面
  const animatedSrc = transferWebP2GIF(animatedUrl, width, quality)
  if (animatedSrc) {
    return animatedSrc
  }

  return transformImage(fallbackUrl, width, quality)
}

export function isVideoUrl(url: string): boolean {
  return isVideoUrlShared(url)
}

export function isVideoFile(file: File): boolean {
  return file.type.startsWith('video/')
}

// 判断颜色是否等价于白色（忽略透明度对 RGB 的影响）
export function isWhiteColor(input?: string | null): boolean {
  if (!input) return false
  if (input === null) return false
  const normalized = input.trim().toLowerCase()
  if (!normalized) return false

  // 直接命名或常见简写/全写
  if (
    normalized === 'white' ||
    normalized === '#fff' ||
    normalized === '#ffffff'
  ) {
    return true
  }

  // 8位 HEX（带透明度，如 #FFFFFFCC）
  if (/^#ffffff[a-f0-9]{2}$/i.test(normalized)) {
    return true
  }

  // rgba 或 rgb 格式
  // 支持: rgb(255,255,255) / rgba(255,255,255,0.8)
  if (
    /^rgba?\(\s*255\s*,\s*255\s*,\s*255\s*(,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      normalized
    )
  ) {
    return true
  }

  // 其他情况尝试用 chroma 解析并判断是否为白色
  try {
    const c = chroma(normalized)
    const [r, g, b] = c.rgb()
    return r === 255 && g === 255 && b === 255
  } catch (e) {
    return false
  }
}

// 判断颜色是否等价于黑色（忽略透明度对 RGB 的影响）
export function isBlackColor(input?: string | null): boolean {
  if (!input) return false
  if (input === null) return false
  const normalized = input.trim().toLowerCase()
  if (!normalized) return false

  // 直接命名或常见简写/全写
  if (
    normalized === 'black' ||
    normalized === '#000' ||
    normalized === '#000000'
  ) {
    return true
  }

  // 8位 HEX（带透明度，如 #000000CC）
  if (/^#000000[a-f0-9]{2}$/i.test(normalized)) {
    return true
  }

  // rgba 或 rgb 格式
  // 支持: rgb(0,0,0) / rgba(0,0,0,0.8)
  if (
    /^rgba?\(\s*0\s*,\s*0\s*,\s*0\s*(,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      normalized
    )
  ) {
    return true
  }

  // 其他情况尝试用 chroma 解析并判断是否为黑色
  try {
    const c = chroma(normalized)
    const [r, g, b] = c.rgb()
    return r === 0 && g === 0 && b === 0
  } catch (e) {
    return false
  }
}
